import * as React from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import ConsumptionChart from './ConsumptionChart';
import ConsumptionDataGrid from './ConsumptionDataGrid';
import TariffDataGrid from './TariffDataGrid';
import CustomDatePicker from './CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import SelectAggregation from './SelectAggregation';
import { DataItem } from "../internals/interfaces";
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandableDataGrid from './ExpandableDataGrid';
import Histogram from './Histogram';
import HistogramSummaryGrid from './HistogramSummaryGrid';
import { PercentileData } from './utils/percentileDataProcessor';
import { useMediaQuery } from '@mui/material';
import ApiClient from '../../utils/apiClient';
import {
  getDailyCacheKey,
  getCachedData,
  setCachedData,
  setDailyCachedData,
  createCacheKeyFromParams,
  getDashboardPreferences,
  setDashboardPreferences
} from '../../utils/cacheUtils';
import { isFreeUser } from '../../utils/subscriptionAccess';

interface TariffDataItem {
  full_name: string;
  start_date: string;
  end_date: string;
  value_exc_vat: number;
  value_inc_vat: number;
  cost_exc_vat: number;
  cost_inc_vat: number;
  payment_method_id: number;
}

export default function ElectricityDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  // Cache key for earliest reading (never expires)
  const getEarliestReadingCacheKey = (): string => {
    return 'earliest-reading-type-1';
  };

  // Get cache date key for most recent complete day (refreshes daily after 6am)
  const getMostRecentCompleteDayCacheKey = (): string => {
    return getDailyCacheKey('most-recent-complete-day-type-1', 6);
  };

  const [electricityLoading, setElectricityLoading] = React.useState(true);
  const [electricityData, setElectricityData] = React.useState<DataItem[]>([]);
  const [tariffData, setTariffData] = React.useState<TariffDataItem[]>([]);
  
  // Load cached preferences on mount
  const cachedPrefs = React.useMemo(() => getDashboardPreferences('electricity'), []);
  const [dateFrom, setDateFrom] = React.useState<Dayjs>(
    cachedPrefs?.dateFrom ? dayjs(cachedPrefs.dateFrom) : dayjs('2024-09-13')
  );
  const [dateTo, setDateTo] = React.useState<Dayjs>(
    cachedPrefs?.dateTo ? dayjs(cachedPrefs.dateTo) : dayjs('2024-09-13')
  );
  const [aggregation, setAggregation] = React.useState<string>(
    cachedPrefs?.aggregation || 'day'
  );
  
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mostRecentCompleteDay, setMostRecentCompleteDay] = React.useState<Dayjs | null>(null);
  const [percentileData, setPercentileData] = React.useState<PercentileData | null>(null);
  const [percentileLoading, setPercentileLoading] = React.useState(true);
  const [controlsExpanded, setControlsExpanded] = React.useState(false);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [refreshData, setRefreshData] = React.useState(false);
  const [days, setDays] = React.useState<string>('all');
  const [histogramDisplayMode, setHistogramDisplayMode] = React.useState<'consumption' | 'cost'>('consumption');
  const [earliestReading, setEarliestReading] = React.useState<Dayjs | null>(null);
  const open = Boolean(anchorEl);

  // Save preferences to cache whenever they change
  React.useEffect(() => {
    if (isInitialized) {
      setDashboardPreferences('electricity', {
        dateFrom: dateFrom.format('YYYY-MM-DD'),
        dateTo: dateTo.format('YYYY-MM-DD'),
        aggregation: aggregation
      });
    }
  }, [dateFrom, dateTo, aggregation, isInitialized]);

  // Listen for dashboard refresh events
  React.useEffect(() => {
    const handleDashboardRefresh = () => {
      setElectricityLoading(true);
      setPercentileLoading(true);
      setRefreshData(false);
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('dashboardRefresh', handleDashboardRefresh);
    return () => {
      window.removeEventListener('dashboardRefresh', handleDashboardRefresh);
    };
  }, []);

  const maxDaysNoAggregation = 0
  const maxDaysDayAggregation = 30

  const handleDateChangeFrom = (x: Dayjs | null) => { 
    const date = x ?? new Dayjs();
    setDateFrom(date);
    if (dateTo < date) {
      setDateTo(date)
    } else if ((aggregation === 'none' || aggregation === 'hour') && dateTo.diff(date, 'day') > maxDaysNoAggregation) {
      setDateTo(date.add(maxDaysNoAggregation, 'day'));
    } else if (aggregation === 'day' && dateTo.diff(date, 'day') > maxDaysDayAggregation) {
      setDateTo(date.add(maxDaysDayAggregation, 'day'));
    }
  };

  const handleDateChangeTo = (x: Dayjs | null) => { 
    const date = x ?? new Dayjs();
    setDateTo(date);
    if (dateFrom > date) {
      setDateFrom(date)
    } else if (aggregation === 'none' && date.diff(dateFrom, 'day') > maxDaysNoAggregation) {
      setDateFrom(date.add(-maxDaysNoAggregation, 'day'));
    } else if (aggregation === 'day' && date.diff(dateFrom, 'day') > maxDaysDayAggregation) {
      setDateFrom(date.add(-maxDaysDayAggregation, 'day'));
    }
  };

  const handleAggregationChange = (newAggregation: string) => {
    setAggregation(newAggregation);
    let currentDate = dayjs(dateTo);
    
    // Ensure currentDate is not later than mostRecentCompleteDay
    if (mostRecentCompleteDay && currentDate.isAfter(mostRecentCompleteDay)) {
      currentDate = mostRecentCompleteDay;
    }
    
    // Update date range based on aggregation type
    if (newAggregation === 'none' || newAggregation === 'hour') {
      // When switching to "None", set dateFrom to match dateTo
      setDateFrom(currentDate);
      setDateTo(currentDate);
    } else if (newAggregation === 'day') {
      // Get the first day (Monday) and last day (Sunday) of the week containing dateFrom      
      setDateFrom(currentDate.add(-6, 'day')); // Monday
      setDateTo(currentDate); // Sunday
    } else if (newAggregation === 'week') {
      // Get the first day (Monday) and last day (Sunday) of the week containing dateFrom
      const startOfWeek = currentDate.startOf('week').add(-3, 'week'); // Monday
      const endOfWeek = currentDate.endOf('week'); // Sunday
      setDateFrom(startOfWeek);
      setDateTo(endOfWeek);
    } else if (newAggregation === 'month') {
      // Get the first and last day of the month containing dateFrom
      const startOfMonth = currentDate.startOf('month').add(-2, 'month');
      const endOfMonth = currentDate.endOf('month');
      setDateFrom(startOfMonth);
      setDateTo(endOfMonth);
    } else if (newAggregation === 'year') {
      // Get the first and last day of the year containing dateFrom
      const startOfYear = currentDate.startOf('year').add(-1, 'year');
      const endOfYear = currentDate.endOf('year');
      setDateFrom(startOfYear);
      setDateTo(endOfYear);
    }
  };

  const handleLatestClick = () => {
    if (mostRecentCompleteDay) {
      // Apply aggregation logic to set appropriate date range
      if (aggregation === 'none' || aggregation === 'hour') {
        // Keep as single day
        setDateFrom(mostRecentCompleteDay);
        setDateTo(mostRecentCompleteDay);
      } else if (aggregation === 'day') {
        // Set the view to the past week
        setDateFrom(mostRecentCompleteDay.add(-6, 'day'));
        setDateTo(mostRecentCompleteDay);
      } else if (aggregation === 'week') {
        // Get the first day (Monday) and last day (Sunday) of the week
        const startOfWeek = mostRecentCompleteDay.startOf('week').add(-3, 'week');
        const endOfWeek = mostRecentCompleteDay.endOf('week');
        setDateFrom(startOfWeek);
        setDateTo(endOfWeek);
      } else if (aggregation === 'month') {
        // Get the first and last day of the month
        const startOfMonth = mostRecentCompleteDay.startOf('month').add(-2, 'month');
        const endOfMonth = mostRecentCompleteDay.endOf('month');
        setDateFrom(startOfMonth);
        setDateTo(endOfMonth);
      } else if (aggregation === 'year') {
        // Get the first and last day of the year
        const startOfYear = mostRecentCompleteDay.startOf('year').add(-1, 'year');
        const endOfYear = mostRecentCompleteDay.endOf('year');
        setDateFrom(startOfYear);
        setDateTo(endOfYear);
      }
    }
  };

  const handleDownloadFullChart = async () => {
    try {
      // Dynamic import of html2canvas to avoid SSR issues
      const html2canvas = (await import('html2canvas')).default as any;
      
      // Get the ConsumptionChart component specifically
      const chartElement = document.querySelector('[data-chart-id="consumption-1"]')?.closest('.MuiCard-root');
      
      if (chartElement) {
        // Hide elements that should be excluded from download
        const excludeElements = chartElement.querySelectorAll('.exclude-from-download');
        const originalDisplays: string[] = [];
        
        excludeElements.forEach((el) => {
          originalDisplays.push((el as HTMLElement).style.display);
          (el as HTMLElement).style.display = 'none';
        });
        
        const canvas = await html2canvas(chartElement as HTMLElement, {
          backgroundColor: theme.palette.background.paper,
          scale: 2, // Higher resolution
          useCORS: true,
          allowTaint: true
        });
        
        // Restore original display values
        excludeElements.forEach((el, index) => {
          (el as HTMLElement).style.display = originalDisplays[index];
        });
        
        // Create download link
        const link = document.createElement('a');
        link.download = `electricity_chart_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (error) {
      console.error('Error downloading electricity chart:', error);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadPNG = () => {
    handleDownloadFullChart();
    handleMenuClose();
  };

  const handleDownloadCSV = () => {
    if (electricityData && electricityData.length > 0) {
      // Create CSV content for consumption data
      const headers = [
        'Time',
        'Consumption (kWh)',
        'Price (p/kWh) - Including VAT',
        'Price (p/kWh) - Excluding VAT',
        'Cost (p) - Including VAT',
        'Cost (p) - Excluding VAT',
        'CO₂ intensity (g/kWh)',
        'CO₂ emissions (g)'
      ];
      
      const csvContent = [
        headers.join(','),
        ...electricityData.map(item => [
          item.reading_time,
          item.consumption || 0,
          item.value_inc_vat || 0,
          item.value_exc_vat || 0,
          item.cost_inc_vat || 0,
          item.cost_exc_vat || 0,
          item.gCO_2_per_kWh || 0,
          item.gCO_2 || 0
        ].join(','))
      ].join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `electricity_consumption_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    handleMenuClose();
  };

  const handleDownloadTariffCSV = () => {
    if (tariffData && tariffData.length > 0) {
      // Create CSV content
      const headers = [
        'Product Name',
        'Start Date',
        'End Date',
        'Standing Charge (p/day) - Excluding VAT',
        'Standing Charge (p/day) - Including VAT',
        'Total Cost (£) - Excluding VAT',
        'Total Cost (£) - Including VAT'
      ];
      
      const csvContent = [
        headers.join(','),
        ...tariffData.map(item => [
          `"${item.full_name || ''}"`,
          item.start_date || '',
          item.end_date || '',
          item.value_exc_vat || 0,
          item.value_inc_vat || 0,
          item.cost_exc_vat || 0,
          item.cost_inc_vat || 0
        ].join(','))
      ].join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `electricity_tariff_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    handleMenuClose();
  };

  // Fetch the earliest reading when component first loads
  React.useEffect(() => {
    let isCancelled = false;
    
    const fetchEarliestReading = async () => {
      try {
        // Check cache first (earliest reading never expires)
        const cacheKey = getEarliestReadingCacheKey();
        const cachedData = getCachedData<{ earliest_date: string }>(cacheKey);
        
        if (cachedData && cachedData.earliest_date) {
          // Use cached data
          if (!isCancelled) {
            const earliestDate = dayjs(cachedData.earliest_date);
            setEarliestReading(earliestDate);
          }
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/userconsumption/earliestReading?type=1');
          
          if (!isCancelled) {
            if (data.earliest_date) {
              // Store in cache
              setCachedData(cacheKey, { earliest_date: data.earliest_date });
              
              const earliestDate = dayjs(data.earliest_date);
              setEarliestReading(earliestDate);
            }
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching earliest reading:", error);
        }
      }
    };

    fetchEarliestReading();
    
    return () => {
      isCancelled = true;
    };
  }, [refreshTrigger]);

  // Fetch the most recent complete day when component first loads
  React.useEffect(() => {
    let isCancelled = false;
    
    const fetchMostRecentCompleteDay = async () => {
      try {
        // Check cache first (refreshes daily after 6am)
        const cacheKey = getMostRecentCompleteDayCacheKey();
        const cachedData = getCachedData<{ most_recent_complete_day: string } | { __empty: true }>(cacheKey);
        
        // Check if we have cached preferences
        const prefs = getDashboardPreferences('electricity');
        
        if (cachedData !== null) {
          // Use cached data (even if empty)
          if (!isCancelled) {
            if ('__empty' in cachedData) {
              // Cached empty result
              setMostRecentCompleteDay(null);
            } else if (cachedData.most_recent_complete_day) {
              const mostRecentDate = dayjs(cachedData.most_recent_complete_day);
              setMostRecentCompleteDay(mostRecentDate);
              // Only set default dates if no cached preferences exist
              if (!prefs) {
                setDateFrom(mostRecentDate.add(-6, 'day'));
                setDateTo(mostRecentDate);
              }
            }
            
            setIsInitialized(true);
            setRefreshData(true);
          }
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/userconsumption/mostRecentCompleteDay?type=1');
          
          if (!isCancelled) {
            // Always cache the result, even if null
            if (data.most_recent_complete_day) {
              // Store in cache with automatic cleanup
              setDailyCachedData(
                cacheKey, 
                { most_recent_complete_day: data.most_recent_complete_day },
                'most-recent-complete-day-type-1',
                6
              );
              
              const mostRecentDate = dayjs(data.most_recent_complete_day);
              setMostRecentCompleteDay(mostRecentDate);
              // Only set default dates if no cached preferences exist
              if (!prefs) {
                setDateFrom(mostRecentDate.add(-6, 'day'));
                setDateTo(mostRecentDate);
              }
            } else {
              // Cache empty result to prevent repeated API calls
              setDailyCachedData(
                cacheKey,
                { __empty: true },
                'most-recent-complete-day-type-1',
                6
              );
              
              setMostRecentCompleteDay(null);
            }
            
            setIsInitialized(true);
            setRefreshData(true);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching most recent complete day:", error);
          setIsInitialized(true);
        }
      }
    };

    fetchMostRecentCompleteDay();
    
    return () => {
      isCancelled = true;
    };
  }, [isInitialized, refreshTrigger]);

  // Fetch electricity consumption data when dates change
  React.useEffect(() => {
    if (!refreshData || !dateFrom || !dateTo) {
      return;
    }

    const fetchData = async () => {      
        try {
          const params = new URLSearchParams({
            date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            type: '1',
            agg: aggregation
          });

          // Check cache first
          const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-get', params);
          const cacheKey = getDailyCacheKey(keyBase, 6);
          const cachedData = getCachedData<DataItem[]>(cacheKey);

          if (cachedData) {
            // Use cached data
            setElectricityData(cachedData);
          } else {
            // Cache miss - fetch from API
            const data: DataItem[] = await ApiClient.get(`api/userconsumption/get?${params}`);
            
            // Store in cache
            setDailyCachedData(cacheKey, data, prefix, 6);
            
            setElectricityData(data);
          }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
          setElectricityLoading(false);
        }
    };

    setElectricityLoading(true);
    fetchData();
  }, [dateFrom, dateTo, aggregation, refreshData]);

  // Fetch standing charges
  React.useEffect(() => {
    if (!refreshData || !dateFrom || !dateTo) {
      return;
    }

    const fetchStandingCharges = async () => {
      try {
        const params = new URLSearchParams({
          date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          type: '1'
        });

        // Check cache first
        const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-standing-charges', params);
        const cacheKey = getDailyCacheKey(keyBase, 6);
        const cachedData = getCachedData<TariffDataItem[]>(cacheKey);

        if (cachedData) {
          // Use cached data
          setTariffData(cachedData);
        } else {
          // Cache miss - fetch from API
          const data: TariffDataItem[] = await ApiClient.get(`api/userconsumption/getStandingCharges?${params}`);
          
          // Store in cache
          setDailyCachedData(cacheKey, data, prefix, 6);
          
          setTariffData(data);
        }
      } catch (error) {
        console.error("Error fetching standing charges:", error);
      }
    };

    fetchStandingCharges();
  }, [dateFrom, dateTo, refreshData]);

  // Fetch percentile data when dates or days filter change
  React.useEffect(() => {
    if (!isInitialized || !dateFrom || !dateTo) {
      return;
    }

    const fetchPercentileData = async () => {
      try {
        const params = new URLSearchParams({
          date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          type: '1',
          hist_type: histogramDisplayMode === 'cost' ? 'cost' : 'consumption',
          days: days.toLowerCase()
        });
        
        // Check cache first
        const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-histogram-percentiles', params);
        const cacheKey = getDailyCacheKey(keyBase, 6);
        const cachedData = getCachedData<PercentileData | { __empty: true }>(cacheKey);

        if (cachedData !== null) {
          // Use cached data (even if empty)
          if ('__empty' in cachedData) {
            setPercentileData(null);
          } else {
            setPercentileData(cachedData);
          }
          setPercentileLoading(false);
        } else if (!isFreeUser()) {
          // Cache miss - fetch from API (premium only)
          const data: PercentileData = await ApiClient.get(`api/userconsumption/histogram_percentiles?${params}`);
       
          // Always cache the result, even if empty
          if (data && Object.keys(data).length > 0) {
            setDailyCachedData(cacheKey, data, prefix, 6);
            setPercentileData(data);
          } else {
            // Cache empty result to prevent repeated API calls
            setDailyCachedData(cacheKey, { __empty: true }, prefix, 6);
            setPercentileData(null);
          }
          setPercentileLoading(false);
        } else {
          setPercentileData(null);
          setPercentileLoading(false);
        }
      } catch (error) {
        console.error('Error fetching percentile data:', error);
        setPercentileData(null);
        setPercentileLoading(false);
      }
    };

    if (!isFreeUser()) {
      setPercentileLoading(true);
      fetchPercentileData();
    } else {
      setPercentileData(null);
      setPercentileLoading(false);
    }
  }, [dateFrom, dateTo, isInitialized, days, histogramDisplayMode]);

  return (
    <Box data-dashboard-id="electricity">
      <Box sx={{ px: { xs: 1, md: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}
          onClick={isXs ? () => setControlsExpanded(!controlsExpanded) : undefined}
        >          
            <Typography variant="h1">
              Your Electricity Usage
            </Typography>
          {isXs && (
            <IconButton
              sx={{ ml: 2 }}
            >
              {controlsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>
        
        {!isInitialized ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading electricity consumption data...
            </Typography>
          </Box>
        ) : !mostRecentCompleteDay ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            mt: 4
          }}>
            <Card sx={{ width: '100%', py: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">
                  No electricity consumption data available
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {(() => {
                    const access = localStorage.getItem("access");
                    const hasPremiumAccess = access && access !== 'null' && access !== 'free';
                    
                    if (hasPremiumAccess) {
                      return "No readings are available from your smart electricity meter.";
                    } else {
                      const threeMonthsAgo = dayjs().subtract(3, 'month').format('MMMM YYYY');
                      return `Your smart electricity meter hasn't submitted any readings since ${threeMonthsAgo}.`;
                    }
                  })()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <>
          <Box sx={{ px: { xs: 1, md: 0 } }}>
            <Box sx={{ 
                width: '100%', 
                maxWidth: '100%',
                mb: 3,
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center'
              }}>
          {/* Desktop layout */}
          <Stack direction="row" sx={{ gap: 1, display: { xs: 'none', sm: 'flex' } }}>
            <Box sx={{ minWidth: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              {aggregation === 'none' || aggregation === 'hour' ? 'Date' : 'Date from'}
            </Typography>
            <CustomDatePicker 
              value={dateFrom} 
              aggregation={aggregation} 
              onDateChange={handleDateChangeFrom}
              minDate={earliestReading}
            />
            </Box>
            {aggregation !== 'none' && aggregation !== 'hour' && (
              <Box sx={{ minWidth: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Date to
              </Typography>
              <CustomDatePicker 
                value={dateTo} 
                aggregation={aggregation} 
                onDateChange={handleDateChangeTo}
                minDate={earliestReading}
              />
              </Box>
            )}
            <SelectAggregation aggregation={aggregation} onChange={handleAggregationChange} />
            <Button
              variant="outlined"
              size="small"
              onClick={handleLatestClick}
              sx={{ 
                minWidth: 'auto',
                height: '40px', // Match the height of the aggregation dropdown
                p: '6px 12px', // Add horizontal padding for the text
                mt: '25px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
              className="exclude-from-download"
            >
              Latest
            </Button>
          </Stack>
          { electricityData.length > 0 && electricityData.some(item => item.consumption !== null) && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleMenuClick}
              disabled={electricityData.length === 0}
              sx={{ 
                minWidth: 'auto',
                height: '40px', // Match the height of the aggregation dropdown
                p: '6px 12px', // Add horizontal padding for the text
                mt: '25px',
                fontSize: '14px',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 0.5
              }}
              className="exclude-from-download"
            >
              <DownloadIcon sx={{ fontSize: '16px' }} />
            </Button>
        )}

          {/* Mobile layout */}
          <Collapse in={controlsExpanded} sx={{ width: '100%' }}>
            <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
            {/* Date pickers - side by side */}
            <Stack direction="row" sx={{ gap: 1, mb: 2 }}>
              <Box sx={{ width: '50%' }}>
                <CustomDatePicker 
                  value={dateFrom} 
                  aggregation={aggregation} 
                  onDateChange={handleDateChangeFrom}
                  label={aggregation === 'none' || aggregation === 'hour' ? 'Date' : 'Date from'}
                  minDate={earliestReading}
                />
              </Box>
              {aggregation !== 'none' && aggregation !== 'hour' && (
                <Box sx={{ width: '50%' }}>
                  <CustomDatePicker 
                    value={dateTo} 
                    aggregation={aggregation} 
                    onDateChange={handleDateChangeTo}
                    label="Date to"
                    minDate={earliestReading}
                  />
                </Box>
              )}
            </Stack>
            
            {/* Buttons row */}
            <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                <SelectAggregation aggregation={aggregation} onChange={handleAggregationChange} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLatestClick}
                  sx={{ 
                    minWidth: 'auto',
                    height: '40px',
                    p: '6px 12px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: '25px'
                  }}
                  className="exclude-from-download"
                >
                  Latest
                </Button>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={handleMenuClick}
                disabled={electricityData.length === 0}
                sx={{ 
                  minWidth: 'auto',
                  height: '40px',
                  p: '6px 12px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: '25px'
                }}
                className="exclude-from-download"
              >
                <DownloadIcon sx={{ fontSize: '16px' }} />
              </Button>
            </Stack>
            </Box>
          </Collapse>
            {anchorEl && (
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleDownloadPNG}>
                  <ListItemIcon>
                    <PictureAsPdfIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download chart (PNG)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownloadCSV}>
                  <ListItemIcon>
                    <TableChartIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download consumption data (CSV)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownloadTariffCSV}>
                  <ListItemIcon>
                    <TableChartIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download tariff data (CSV)</ListItemText>
                </MenuItem>
              </Menu>
            )}
          </Box>
        </Box>
        
        <ConsumptionChart 
          dateFrom={dateFrom.format('YYYY-MM-DD HH:mm:ss')} 
          dateTo={dateTo.format('YYYY-MM-DD HH:mm:ss')} 
          consumptionType={1} 
          aggregation={aggregation} 
          data={electricityData} 
          loading={electricityLoading} 
          tariffs={tariffData} 
          data-chart-id="consumption-1"
        />
                
        {/* Consumption Data Grid - Different layout based on user access level */}
        {isFreeUser() ? (
          // Direct display without expansion for free users
          <>
            <Box sx={{ px: { xs: 1, md: 0 } }}>
              <Typography variant="h3" sx={{ mb: 2, mt: 2 }}>
                Your tariff
              </Typography>          
            </Box>

            <Grid container spacing={{ xs: 0, sm: 0, md: 2 }} columns={12} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TariffDataGrid data={tariffData} loading={electricityLoading} />
              </Grid>
            </Grid>
            {electricityData.length > 0 && electricityData.some(item => item.consumption !== null) && (
              <>
                <Box sx={{ px: { xs: 1, md: 0 } }}>
                  <Typography variant="h3" sx={{ mb: 2, mt: 2 }}>
                    Electricity Consumption
                  </Typography>
                </Box>

                <ConsumptionDataGrid data={electricityData} loading={electricityLoading} aggregation={aggregation} />
              </>
            )}
          </>
        ) : (
          // Expandable view for premium users
          <ExpandableDataGrid title={isMobile ? 'Consumption Details' : 'Electricity Consumption Details'}>
            <Box sx={{ px: { xs: 1, md: 0 } }}>
              <Typography variant="h3" sx={{ mb: 2, mt: 2 }}>
                Your tariff
              </Typography>          
            </Box>

            <Grid container spacing={{ xs: 0, sm: 0, md: 2 }} columns={12} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TariffDataGrid data={tariffData} loading={electricityLoading} />
              </Grid>
            </Grid>
            { electricityData.length > 0 && electricityData.some(item => item.consumption !== null) && (
              <>
                <Box sx={{ px: { xs: 1, md: 0 } }}>
                  <Typography variant="h3" sx={{ mb: 2, mt: 2 }}>
                    Electricity Consumption
                  </Typography>
                </Box>

                <ConsumptionDataGrid data={electricityData} loading={electricityLoading} aggregation={aggregation} />
              </>
            )}
          </ExpandableDataGrid>
        )}
        
        {/* Hide Histogram sections for free users */}
        {!isFreeUser() && electricityData.some(item => item.consumption !== null) && (
          <>
            {/* Electricity Consumption Histogram */}
            <Grid
              container
              spacing={{ xs: 0, sm: 0, md: 2 }}
              columns={12}
              sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Histogram 
                  dateFrom={dateFrom.format('YYYY-MM-DD HH:mm:ss')} 
                  dateTo={dateTo.format('YYYY-MM-DD HH:mm:ss')} 
                  consumptionType={1} 
                  aggregation={aggregation} 
                  loading={electricityLoading || percentileLoading} 
                  percentileData={percentileData}
                  histType='consumption'
                  isFreeUser={isFreeUser()}
                  days={days}
                  onDaysChange={setDays}
                  displayMode={histogramDisplayMode}
                  onDisplayModeChange={setHistogramDisplayMode}
                />
              </Grid>
            </Grid>

            {/* Electricity Consumption Summary Grid */}
            <Grid
              container
              spacing={{ xs: 0, sm: 0, md: 2 }}
              columns={12}
              sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <HistogramSummaryGrid 
                  percentileData={percentileData}
                  loading={electricityLoading || percentileLoading}
                  consumptionType={1}
                  displayMode={histogramDisplayMode}
                />
              </Grid>
            </Grid>
          </>
        )}
          </>
        )}
      </Box>
  );
}
